import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Alert,
  Chip,
  Box,
  Divider,
  Tooltip,
  MenuItem,
  Select,
  CircularProgress,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Lan as LanIcon,
  Cloud as CloudIcon,
  RestartAlt as ResetIcon,
  Build as BuildIcon,
  Replay as ReplayIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { Project } from '../../../models/Project';
import { ServiceDeployConfig, SITE_BATCH_SERVICE_CATALOG } from '../../../models/SiteBatch';
import { DeployTabType } from '../types';
import ServiceSelectionTable from '../widgets/ServiceSelectionTable';
import DeployConfigFiles from '../widgets/DeployConfigFiles';
import { useDeployServiceSelection } from '../hooks/useDeployServiceSelection';
import { integrationServers, remoteDefaultSelection, serverLabel } from '../hooks/remoteDeploy';
import { SiteNetworkDeviceService } from '../../../services/SiteNetworkDeviceService';
import { RuntimeService } from '../../../services/RuntimeService';

const TARGET_META: Record<
  DeployTabType,
  { label: string; color: 'primary' | 'warning'; Icon: typeof LanIcon; title: string; sub: string }
> = {
  company: {
    label: '사내망 (Company)',
    color: 'primary',
    Icon: LanIcon,
    title: '사내 테스트 서버 배포',
    sub: '빌드해서 사내 서버(102)에 반영 — 현장에는 영향 없음',
  },
  remote: {
    label: '현장망 (Remote)',
    color: 'warning',
    Icon: CloudIcon,
    title: '현장 통합서버 배포',
    sub: '실제 현장 서버에 반영됩니다',
  },
};

interface DeployConfirmDialogProps {
  open: boolean;
  site: Project | null;
  activeSiteName: string | null;
  branch?: string;
  target?: DeployTabType;
  onClose: () => void;
  /**
   * Called with per-service deploy configs (deploy-service-mode-select).
   * When the operator leaves every service in build mode, `services` is
   * an empty array — the caller MUST treat that as "use backend default
   * (all 8 services, build mode)" to preserve backward compatibility
   * with the legacy deploy flow.
   *
   * Resolves → dialog closes. Rejects → in-dialog error alert is shown
   * and the dialog stays open.
   *
   * target=remote 이면 options.targetDeviceId(고른 현장 통합서버)가 같이 온다.
   */
  onConfirm: (
    site: Project,
    services: ServiceDeployConfig[],
    options?: DeployConfirmOptions,
  ) => Promise<void>;
}

export interface DeployConfirmOptions {
  /** 원격 배포 대상 — 현장 장비(integration-server) id */
  targetDeviceId?: number;
}

const APPNAMES = SITE_BATCH_SERVICE_CATALOG.map((s) => s.appname);

const DeployConfirmDialog: React.FC<DeployConfirmDialogProps> = ({
  open,
  site,
  activeSiteName,
  branch = 'develop',
  target = 'company',
  onClose,
  onConfirm,
}) => {
  const targetMeta = TARGET_META[target];
  const TargetIcon = targetMeta.Icon;
  const isRemote = target === 'remote';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetDeviceId, setTargetDeviceId] = useState<number | null>(null);

  const { selection, setSelection, updateService, reset, setAllMode, isReady, toPayload } =
    useDeployServiceSelection(APPNAMES);

  // 원격: 이 현장의 통합서버 목록과, 사내 서버에 지금 떠 있는 버전(승격 기본값).
  // 사내 버전 query key 는 배포 화면의 실행 중 버전 패널과 같아 캐시를 함께 쓴다.
  const projectIdNum = Number(site?.id);
  const devicesQuery = useQuery({
    queryKey: ['site-network-devices', projectIdNum],
    queryFn: () => SiteNetworkDeviceService.list(projectIdNum),
    enabled: open && isRemote && Number.isFinite(projectIdNum),
  });
  const servers = useMemo(() => integrationServers(devicesQuery.data ?? []), [devicesQuery.data]);
  const companyQuery = useQuery({
    queryKey: ['runtime', 'service-versions'],
    queryFn: ({ signal }) => RuntimeService.getServiceVersions(signal),
    enabled: open && isRemote,
    retry: false,
  });
  // 사내 서버는 한 번에 한 현장만 띄운다 — 이 현장이 떠 있을 때만 그 버전을 기본값으로 쓴다.
  const companyVersions =
    companyQuery.data && companyQuery.data.site === site?.name ? companyQuery.data.versions : null;

  // Each time the dialog opens: clear any prior error and reset all rows
  // to build mode. This mirrors the old "always start from full-build"
  // UX so an operator's previous reuse selections don't leak into the
  // next site's deploy flow.
  useEffect(() => {
    if (open) {
      setError(null);
      reset();
      setTargetDeviceId(null);
    }
  }, [open, reset]);

  // 원격은 열 때 한 번 사내 버전으로 채운다(사내 버전 조회가 끝난 뒤). 채우기 전에는 표를
  // 감춰 운영자가 고른 값이 나중에 덮이지 않게 한다.
  const [remoteDefaultsReady, setRemoteDefaultsReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setRemoteDefaultsReady(false);
      return;
    }
    if (!isRemote || remoteDefaultsReady || companyQuery.isFetching) return;
    setSelection(remoteDefaultSelection(APPNAMES, companyVersions));
    setRemoteDefaultsReady(true);
  }, [open, isRemote, remoteDefaultsReady, companyQuery.isFetching, companyVersions, setSelection]);
  const selectionReady = !isRemote || remoteDefaultsReady;

  // 통합서버가 하나뿐이면 그걸 고른다 — 방재실이 여럿인 현장만 직접 고르게.
  useEffect(() => {
    if (open && isRemote && targetDeviceId === null && servers.length === 1) {
      setTargetDeviceId(servers[0].id);
    }
  }, [open, isRemote, servers, targetDeviceId]);

  const needsServer = isRemote && targetDeviceId === null;
  const canSubmit = !submitting && !!site && isReady && !needsServer && selectionReady;

  // deploy-service-skip: explain to the operator *why* the deploy button
  // is disabled. isReady is false in two cases:
  //   1. A reuse-mode row has no target_version picked yet
  //   2. Every row is in "배포안함" (skip) — nothing left to deploy
  // `site` being null is a wiring edge case and doesn't need a tooltip.
  const allSkipped =
    !!site &&
    Array.from(selection.values()).every((s) => s.mode === 'skip');
  const disabledTooltip = !site
    ? ''
    : submitting
      ? ''
      : allSkipped
        ? '최소 1개 이상의 서비스를 배포해야 합니다'
        : !isReady
          ? '재사용 모드 서비스의 버전을 선택해주세요'
          : needsServer
            ? '배포할 현장 통합서버를 골라주세요'
            : '';

  const handleConfirm = useCallback(async () => {
    if (!site) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isRemote && targetDeviceId !== null) {
        await onConfirm(site, toPayload(), { targetDeviceId });
      } else {
        await onConfirm(site, toPayload());
      }
      onClose();
    } catch (e) {
      console.error('배포 트리거 실패:', e);
      // 백엔드가 거절 사유를 주면(원격: 승격할 수 없는 버전·대상 서버 등) 그대로 보여준다.
      const reason = axios.isAxiosError(e) ? e.response?.data?.message : undefined;
      setError(reason || '배포 트리거에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }, [site, onConfirm, toPayload, onClose, isRemote, targetDeviceId]);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-describedby="deploy-confirm-desc"
      // 사내/현장을 다이얼로그 테두리 색부터 다르게 — 어디로 가는지 첫눈에 보여야 한다.
      PaperProps={{ sx: { borderTop: 6, borderColor: `${targetMeta.color}.main` } }}
    >
      <DialogTitle
        data-testid="deploy-confirm-band"
        data-target={target}
        sx={{ bgcolor: `${targetMeta.color}.main`, color: `${targetMeta.color}.contrastText`, py: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <TargetIcon />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ display: 'block', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.3 }}>
              {targetMeta.title}
            </Typography>
            <Typography component="span" sx={{ display: 'block', fontSize: '0.8rem', opacity: 0.9 }}>
              {targetMeta.sub}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent id="deploy-confirm-desc">
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              대상 현장
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {site?.name ?? '—'}
            </Typography>
            {site?.location && (
              <Typography variant="body2" color="text.secondary">
                {site.location}
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              대상 네트워크
            </Typography>
            <Chip
              icon={<TargetIcon fontSize="small" />}
              label={targetMeta.label}
              color={targetMeta.color}
              size="small"
              data-testid="deploy-target-chip"
            />
          </Box>

          {isRemote ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                대상 통합서버
              </Typography>
              {devicesQuery.isLoading ? (
                <CircularProgress size={18} />
              ) : devicesQuery.isError ? (
                <Alert severity="error">현장 장비 목록을 불러오지 못했습니다.</Alert>
              ) : servers.length === 0 ? (
                <Alert severity="warning" data-testid="deploy-target-server-missing">
                  현장 설정에 통합서버(integration-server)가 등록돼 있지 않습니다. 현장 설정의
                  네트워크 장비에서 먼저 등록해주세요.
                </Alert>
              ) : (
                <Select
                  size="small"
                  value={targetDeviceId ?? ''}
                  displayEmpty
                  onChange={(e) => setTargetDeviceId(Number(e.target.value))}
                  disabled={submitting}
                  sx={{ minWidth: { xs: '100%', sm: 320 } }}
                  inputProps={{ 'aria-label': '대상 통합서버' }}
                  data-testid="deploy-target-server"
                >
                  <MenuItem value="" disabled>
                    통합서버 선택
                  </MenuItem>
                  {servers.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {serverLabel(d)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                브랜치
              </Typography>
              <Chip label={branch} size="small" />
            </Box>
          )}

          {target === 'remote' && (
            <Alert severity="warning" data-testid="deploy-target-remote-warning">
              <strong>현장망(Remote)</strong>으로 배포합니다. 실제 현장 서버에 영향이 갑니다.
              대상 현장과 네트워크를 다시 한 번 확인해주세요.
            </Alert>
          )}

          {activeSiteName && (
            <Alert severity="warning">
              현재 <strong>{activeSiteName}</strong> 이(가) 구동 중입니다.
              구동 시 롤링 배포로 <strong>{site?.name}</strong>(으)로 교체됩니다.
            </Alert>
          )}

          {isRemote && (
            <Alert severity="info" data-testid="deploy-remote-info">
              사내 서버에서 확인한 이미지 버전을 현장 통합서버에 그대로 올립니다(현장에서 새로
              빌드하지 않음). 기본값은 사내 서버에서 이 현장으로 떠 있는 버전입니다. 서비스마다 이전
              이미지를 남기고, 재기동 확인에 실패하면 자동으로 되돌립니다. map 은 이 현장으로 빌드한
              이미지만 올라갑니다.
            </Alert>
          )}

          {!activeSiteName && !isRemote && (
            <Alert severity="info">
              GitHub Actions 를 통해 배포가 트리거됩니다. 기본은 전체 빌드 배포이며,
              필요한 서비스만 "재사용"으로 바꿔 기존 이미지를 재배포할 수 있습니다.
            </Alert>
          )}

          <Divider />

          {/* 배포 서비스와 그 서비스들이 읽는 설정 파일을 나란히 — 한눈에 확인하고 올린다. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  배포 서비스 (모드 선택)
                </Typography>
                {/* 일괄 선택 — 서비스가 많을 때 한 번에 모드 변경. reuse 일괄 적용
                    시에는 row 별 버전을 다시 골라야 하므로 isReady=false 가 된다. */}
                <Stack direction="row" spacing={0.5}>
                  {isRemote ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ReplayIcon fontSize="small" />}
                      onClick={() => setSelection(remoteDefaultSelection(APPNAMES, companyVersions))}
                      disabled={submitting || !site}
                      data-testid="deploy-bulk-company"
                    >
                      사내 버전으로
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<BuildIcon fontSize="small" />}
                        onClick={() => setAllMode('build', site?.name)}
                        disabled={submitting || !site}
                        data-testid="deploy-bulk-build"
                      >
                        전체 빌드
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon fontSize="small" />}
                        onClick={() => setAllMode('reuse', site?.name)}
                        disabled={submitting || !site}
                        data-testid="deploy-bulk-reuse"
                      >
                        전체 재사용
                      </Button>
                    </>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockIcon fontSize="small" />}
                    onClick={() => setAllMode('skip', site?.name)}
                    disabled={submitting || !site}
                    data-testid="deploy-bulk-skip"
                  >
                    전체 배포안함
                  </Button>
                </Stack>
              </Stack>
              {selectionReady ? (
                <ServiceSelectionTable
                  projectId={site?.id ?? null}
                  site={site?.name ?? ''}
                  selection={selection}
                  onServiceChange={updateService}
                  remote={isRemote}
                />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    사내 서버에 떠 있는 버전을 확인하는 중…
                  </Typography>
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {isRemote
                  ? '"승격" 서비스는 버전을 지정해야 배포 버튼이 활성화됩니다. 서비스는 하나씩 차례로 교체되고, 일부가 실패해도 나머지는 계속 진행됩니다.'
                  : '"재사용" 선택 시 버전을 지정해야 배포 버튼이 활성화됩니다. 일부 서비스가 실패해도 나머지는 계속 배포됩니다 (continue-on-failure).'}
              </Typography>
            </Box>
            <DeployConfigFiles projectId={site ? Number(site.id) || null : null} target={target} selection={selection} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          onClick={reset}
          disabled={submitting}
          data-testid="deploy-confirm-reset"
        >
          초기화
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose} disabled={submitting}>
            취소
          </Button>
          <Tooltip
            title={disabledTooltip}
            disableHoverListener={!disabledTooltip}
          >
            {/* span wrapper — MUI Tooltip requires a non-disabled child to
                trigger the hover listener. Without it, the disabled button
                would swallow mouse events and the tooltip would never open. */}
            <span>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color={targetMeta.color}
                // 테마의 contained 파란 그라데이션을 대상 색으로 덮는다 — 현장 배포 버튼은 주황.
                sx={{
                  background: (t) => t.palette[targetMeta.color].main,
                  '&:hover': { background: (t) => t.palette[targetMeta.color].dark },
                  '&.Mui-disabled': { background: (t) => t.palette.action.disabledBackground },
                }}
                disabled={!canSubmit}
                startIcon={<RocketIcon />}
                data-testid="deploy-confirm-submit"
              >
                {submitting ? '트리거 중...' : isRemote ? '현장에 배포' : '구동 시작'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DeployConfirmDialog;
